import { Box, Stack, Typography } from '@mui/material'
import SectionContainer from './SectionContainer'
import { SectionTemplate } from '../types'
import { PATH_COLORS } from '../palette'

interface Props {
  template: SectionTemplate
  values: { laneDescriptor?: string; evidenceAnchors?: string[]; conditionsForStrength?: string[] }
  title: string
  description: string
  sectionNumber?: number
}

export default function NarrativeSection({ template, values, title, description, sectionNumber }: Props) {
  const { laneDescriptor, evidenceAnchors = [], conditionsForStrength = [] } = values || {}

  return (
    <SectionContainer
      id={template.sectionTypeId}
      number={sectionNumber}
      title={title}
      description={description}
    >
      <Stack spacing={5}>
        {laneDescriptor && (
          <Box
            sx={{
              position: 'relative',
              pl: { xs: 3, md: 4 },
              borderLeft: `3px solid ${PATH_COLORS[0]}`,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'var(--ns-display-font)',
                fontSize: { xs: 20, md: 26 },
                lineHeight: 1.4,
                fontWeight: 400,
                maxWidth: 880,
              }}
            >
              {laneDescriptor}
            </Typography>
          </Box>
        )}
        {evidenceAnchors.length > 0 && (
          <Stack spacing={2}>
            <Typography variant="overline" sx={{ letterSpacing: 2, color: PATH_COLORS[0], fontWeight: 700 }}>
              Evidence
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 3,
              }}
            >
              {evidenceAnchors.map((anchor, i) => {
                const accent = PATH_COLORS[i % PATH_COLORS.length]
                return (
                  <Box
                    key={i}
                    sx={{
                      p: 3,
                      background: 'rgba(255,255,255,0.7)',
                      borderTop: `3px solid ${accent}`,
                      borderRight: 1,
                      borderBottom: 1,
                      borderLeft: 1,
                      borderRightColor: 'divider',
                      borderBottomColor: 'divider',
                      borderLeftColor: 'divider',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'var(--ns-display-font)',
                        fontSize: 28,
                        fontWeight: 500,
                        color: accent,
                        lineHeight: 1,
                      }}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1.5, lineHeight: 1.55 }}>
                      {anchor}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </Stack>
        )}
        {conditionsForStrength.length > 0 && (
          <Stack spacing={1.5}>
            <Typography variant="overline" sx={{ letterSpacing: 2, color: PATH_COLORS[0], fontWeight: 700 }}>
              Conditions for Strength
            </Typography>
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              {conditionsForStrength.map((c, i) => (
                <Box component="li" key={i} sx={{ mb: 0.75, '&::marker': { color: PATH_COLORS[0] } }}>
                  <Typography variant="body2">{c}</Typography>
                </Box>
              ))}
            </Box>
          </Stack>
        )}
      </Stack>
    </SectionContainer>
  )
}
